import { type ReactElement } from "react";
import {
  type PresenceInputSubscribe,
  type PresencePageSource,
  PresenceRecorderCore,
} from "@client/presence/PresenceRecorder";
import { iphonePageFamilyFor } from "@lib/usageMetrics/pages";
import { useIPhoneCurrentUser } from "./hooks/useIPhoneCurrentUser";
import { getSelectedRoundId, subscribeSelectedRound } from "./lib/iphoneRound";
import { getStack, subscribeNavigationStack } from "./lib/navigationStack";

/**
 * This shell navigates its own stack (`navigationStack.ts`), never the
 * browser history, so the browser page source would record every sitting
 * under the WebView's initial URL. The top of the stack is the screen, and
 * the selected round is its round — switching rounds is a page change too.
 *
 * One navigation can change both (switching rounds resets the stack, then
 * selects the round), so changes are read once the current task is done:
 * reading after each would record a zero-length page in between.
 */
export const stackPageSource: PresencePageSource = {
  current: () => {
    const entries = getStack();
    return iphonePageFamilyFor(
      entries[entries.length - 1]?.path ?? "/",
      getSelectedRoundId(),
    );
  },
  subscribe: (onChange) => {
    const notifier = notifyOncePerTask(onChange);
    const unsubscribeStack = subscribeNavigationStack(notifier.notify);
    const unsubscribeRound = subscribeSelectedRound(notifier.notify);

    return () => {
      notifier.cancel();
      unsubscribeStack();
      unsubscribeRound();
    };
  },
};

/**
 * Native playback is this shell's hands-off watching: AVPlayer plays outside
 * the WebView, so no `playing` / `timeupdate` ever reaches the document and
 * the generic recorder would see a person watching a whole video as idle.
 * The plugin's ~1 Hz progress event is the equivalent evidence; the builder
 * keeps one offset per minute, so the rate costs nothing. Module-level so
 * the recorder's effect dependency is stable.
 */
export const subscribeNativePlayback: PresenceInputSubscribe = (note) => {
  let unsubscribed = false;
  let remove: (() => Promise<void>) | null = null;
  // The plugin module is loaded here, not at import: importing this wrapper
  // must not evaluate the Capacitor plugin (a DOM test that imports the
  // wrapper would otherwise pull the real plugin into the process ahead of
  // other suites' mocks of it). Presence must never fail because the player
  // did: a runtime without the plugin rejects, and that is not the
  // recorder's concern — there is simply no native playback to observe.
  void import("./lib/NativeVideoPlayer")
    .then(({ NativeVideoPlayer }) =>
      NativeVideoPlayer.addListener("playbackProgress", () => {
        note("deliberate");
      }),
    )
    .then((handle) => {
      if (unsubscribed) {
        void handle.remove();
      } else {
        remove = () => handle.remove();
      }
    })
    .catch(() => {});

  return () => {
    unsubscribed = true;
    void remove?.().catch(() => {});
  };
};

/**
 * The iPhone shell's presence recorder. Its identity is the shell's own —
 * the user cached from the last successful sign-in while offline — so a
 * cold offline launch records presence exactly as the auth gate admits the
 * user. The shared current-user query, which the web recorder reads, is
 * disabled while this shell is offline and would leave presence unrecorded
 * for the whole sitting.
 */
export function IPhonePresenceRecorder({
  subscribeInput = subscribeNativePlayback,
  pageSource = stackPageSource,
}: {
  /** Test seam: the DOM suite cannot reach the native plugin, and a module
   *  mock of it would collide with the other DOM tests' mocks of the same
   *  module (mocks are process-global). Production uses the bridge above. */
  subscribeInput?: PresenceInputSubscribe;
  pageSource?: PresencePageSource;
} = {}): ReactElement {
  const { user } = useIPhoneCurrentUser();
  return (
    <PresenceRecorderCore
      userId={user?.userId ?? null}
      subscribeInput={subscribeInput}
      pageSource={pageSource}
    />
  );
}

function notifyOncePerTask(onChange: () => void) {
  let isPending = false;
  let isCancelled = false;

  return {
    notify: () => {
      if (isPending) {
        return;
      }

      isPending = true;
      queueMicrotask(() => {
        isPending = false;

        if (!isCancelled) {
          onChange();
        }
      });
    },
    cancel: () => {
      isCancelled = true;
    },
  };
}
